import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Agent {
  id: string;
  name: string;
  short_description?: string;
  description?: string;
  avatar_emoji?: string;
  color?: string;
  category?: string;
  model: string;
  instructions: string;
  temperature: number;
  visibility: 'private' | 'team' | 'organization';
  is_published: boolean;
  is_featured: boolean;
  is_system: boolean;
  usage_count: number;
  avg_rating: number;
  total_ratings: number;
  personality?: { tone: string; verbosity: string; proactivity: string };
  product_scopes?: string[];
  enabled_tools?: string[];
  require_confirmation: boolean;
  conversation_starters?: Array<{ title: string; prompt: string }>;
  created_by: string;
  created_at: string;
}

interface Conversation {
  id: string;
  title: string;
  message_count: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  tokens_input?: number;
  tokens_output?: number;
  created_at: string;
}

export function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [activeTab, setActiveTab] = useState<'chat' | 'knowledge' | 'analytics'>('chat');

  const { data: agent, isLoading: agentLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => api<Agent>(`custom-gpts/${agentId}`),
    enabled: Boolean(agentId),
  });

  const { data: convData } = useQuery({
    queryKey: ['agent-conversations', agentId],
    queryFn: () => api<{ conversations: Conversation[] }>(`custom-gpts/${agentId}/conversations`),
    enabled: Boolean(agentId),
  });

  const { data: messagesData, isFetching: messagesFetching } = useQuery({
    queryKey: ['agent-messages', agentId, activeConvId],
    queryFn: () => api<{ messages: Message[] }>(`custom-gpts/${agentId}/conversations/${activeConvId}/messages`),
    enabled: Boolean(agentId) && Boolean(activeConvId),
  });

  const { data: knowledgeData } = useQuery({
    queryKey: ['agent-knowledge', agentId],
    queryFn: () => api<{ url_sources: Array<{ id: string; url: string; status: string; chunk_count: number }>; file_sources: Array<{ source_id: string; filename: string; chunk_count: number }>; total_chunks: number }>(`custom-gpts/${agentId}/knowledge`),
    enabled: Boolean(agentId) && activeTab === 'knowledge',
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['agent-analytics', agentId],
    queryFn: () => api<{ summary: { total_conversations: number; unique_users: number; total_messages: number; total_tokens: number; avg_rating: number }; daily_usage: Array<{ date: string; conversations: number; messages: number }> }>(`custom-gpts/${agentId}/analytics`),
    enabled: Boolean(agentId) && activeTab === 'analytics',
  });

  const createConvMutation = useMutation({
    mutationFn: (title: string) => api<{ id: string }>(`custom-gpts/${agentId}/conversations`, { body: { title } }),
    onSuccess: (data) => {
      setActiveConvId(data.id);
      queryClient.invalidateQueries({ queryKey: ['agent-conversations', agentId] });
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (content: string) =>
      api<{ message: Message }>(`custom-gpts/${agentId}/conversations/${activeConvId}/messages`, { body: { content } }),
    onSuccess: () => {
      setMessageInput('');
      queryClient.invalidateQueries({ queryKey: ['agent-messages', agentId, activeConvId] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => api(`custom-gpts/${agentId}/publish`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent', agentId] }),
  });

  const handleSend = () => {
    if (!messageInput.trim()) return;
    if (!activeConvId) {
      // Auto-create a conversation
      createConvMutation.mutate(messageInput.slice(0, 50), {
        onSuccess: (data) => {
          sendMessageMutation.mutate(messageInput.trim());
          setActiveConvId(data.id);
        },
      });
    } else {
      sendMessageMutation.mutate(messageInput.trim());
    }
  };

  if (agentLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading agent...</div>;
  }

  if (!agent) {
    return <div className="p-8 text-center text-muted-foreground">Agent not found.</div>;
  }

  const conversations = convData?.conversations ?? [];
  const messages = messagesData?.messages ?? [];

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Left sidebar */}
      <div className="w-72 border-r flex flex-col shrink-0">
        {/* Agent header */}
        <div className="p-4 border-b">
          <button onClick={() => navigate('/agents')} className="text-xs text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1">
            ← Agents
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
              style={{ backgroundColor: agent.color ? `${agent.color}20` : 'rgb(var(--primary) / 0.1)' }}>
              {agent.avatar_emoji ?? '🤖'}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{agent.name}</div>
              <div className="text-xs text-muted-foreground">{agent.model}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b text-xs">
          {(['chat', 'knowledge', 'analytics'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 capitalize font-medium transition-colors ${
                activeTab === tab ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'chat' && (
          <div className="flex-1 overflow-y-auto p-3">
            <button
              onClick={() => createConvMutation.mutate('New Conversation')}
              className="w-full text-sm border rounded-md py-2 hover:bg-muted mb-3 font-medium"
            >
              + New Conversation
            </button>
            <div className="space-y-1">
              {conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => setActiveConvId(conv.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    activeConvId === conv.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <div className="truncate font-medium">{conv.title}</div>
                  <div className="text-xs text-muted-foreground">{conv.message_count} messages</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'knowledge' && (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="text-xs font-medium text-muted-foreground uppercase mb-2">Total Chunks</div>
            <div className="text-2xl font-bold mb-4">{knowledgeData?.total_chunks ?? 0}</div>

            {knowledgeData?.url_sources && knowledgeData.url_sources.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-muted-foreground uppercase mb-2">URL Sources</div>
                <div className="space-y-2">
                  {knowledgeData.url_sources.map(src => (
                    <div key={src.id} className="p-2 rounded border text-xs">
                      <div className="truncate text-foreground">{src.url}</div>
                      <div className={`text-xs mt-0.5 ${src.status === 'ready' ? 'text-green-600' : src.status === 'failed' ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {src.status} · {src.chunk_count} chunks
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {knowledgeData?.file_sources && knowledgeData.file_sources.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase mb-2">Files</div>
                <div className="space-y-2">
                  {knowledgeData.file_sources.map(src => (
                    <div key={src.source_id} className="p-2 rounded border text-xs">
                      <div className="truncate">{src.filename}</div>
                      <div className="text-muted-foreground">{src.chunk_count} chunks</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!agent.is_system && (
              <button
                onClick={() => navigate(`/agents/${agentId}/edit`)}
                className="w-full mt-4 text-sm border rounded-md py-2 hover:bg-muted"
              >
                Manage Knowledge →
              </button>
            )}
          </div>
        )}

        {activeTab === 'analytics' && analyticsData && (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Conversations', value: analyticsData.summary.total_conversations },
                { label: 'Unique Users', value: analyticsData.summary.unique_users },
                { label: 'Total Messages', value: analyticsData.summary.total_messages },
                { label: 'Avg Rating', value: analyticsData.summary.avg_rating.toFixed(1) },
              ].map(stat => (
                <div key={stat.label} className="p-2 rounded-lg border bg-card text-center">
                  <div className="text-lg font-bold">{stat.value}</div>
                  <div className="text-[10px] text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>

            {!agent.is_system && (
              <button
                onClick={() => navigate(`/agents/${agentId}/analytics`)}
                className="w-full text-sm border rounded-md py-2 hover:bg-muted"
              >
                Full Analytics →
              </button>
            )}
          </div>
        )}

        {/* Agent actions */}
        {!agent.is_system && (
          <div className="p-3 border-t space-y-2">
            <button
              onClick={() => navigate(`/agents/${agentId}/edit`)}
              className="w-full text-sm border rounded-md py-2 hover:bg-muted"
            >
              Edit Agent
            </button>
            {!agent.is_published && (
              <button
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
                className="w-full text-sm bg-primary text-primary-foreground rounded-md py-2 hover:bg-primary/90 disabled:opacity-50"
              >
                {publishMutation.isPending ? 'Publishing...' : 'Publish to Org'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {!activeConvId ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4"
              style={{ backgroundColor: agent.color ? `${agent.color}20` : 'rgb(var(--primary) / 0.1)' }}>
              {agent.avatar_emoji ?? '🤖'}
            </div>
            <h2 className="text-xl font-bold mb-2">{agent.name}</h2>
            <p className="text-muted-foreground text-sm mb-6 max-w-md">
              {agent.short_description ?? agent.description ?? 'Ready to assist'}
            </p>

            {agent.product_scopes && agent.product_scopes.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1 mb-6">
                {agent.product_scopes.map(scope => (
                  <span key={scope} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase font-medium">
                    {scope}
                  </span>
                ))}
              </div>
            )}

            {agent.conversation_starters && agent.conversation_starters.length > 0 && (
              <div className="grid grid-cols-1 gap-2 w-full max-w-lg">
                {agent.conversation_starters.map((starter, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setMessageInput(starter.prompt);
                    }}
                    className="text-left p-3 rounded-lg border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                  >
                    <div className="text-sm font-medium text-foreground">{starter.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{starter.prompt}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm mr-2 shrink-0 mt-0.5"
                    style={{ backgroundColor: agent.color ? `${agent.color}20` : 'rgb(var(--primary) / 0.1)' }}>
                    {agent.avatar_emoji ?? '🤖'}
                  </div>
                )}
                <div className={`max-w-2xl rounded-2xl px-4 py-3 text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && msg.tokens_input && (
                    <p className="text-[10px] opacity-50 mt-1">
                      {msg.tokens_input + (msg.tokens_output ?? 0)} tokens
                    </p>
                  )}
                </div>
              </div>
            ))}
            {(sendMessageMutation.isPending || messagesFetching) && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm mr-2 shrink-0"
                  style={{ backgroundColor: agent.color ? `${agent.color}20` : 'rgb(var(--primary) / 0.1)' }}>
                  {agent.avatar_emoji ?? '🤖'}
                </div>
                <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    {[1,2,3].map(i => <div key={i} className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: `${i * 0.1}s` }} />)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input */}
        <div className="border-t p-4">
          <div className="flex gap-2 items-end">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder={`Message ${agent.name}...`}
              rows={1}
              className="flex-1 border rounded-xl px-4 py-3 bg-background text-sm resize-none min-h-[48px] max-h-32"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              onClick={handleSend}
              disabled={!messageInput.trim() || sendMessageMutation.isPending}
              className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 hover:bg-primary/90 shrink-0"
            >
              ↑
            </button>
          </div>
          {agent.require_confirmation && (
            <p className="text-xs text-amber-600 mt-1">This agent will ask for confirmation before making any changes.</p>
          )}
        </div>
      </div>
    </div>
  );
}
